import { useState, useEffect, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { vendorApi } from '../services/vendor';
import extractApiError from '../utils/extractApiError';
import type { SiteListItem } from '../types';

interface GuardFormModalProps {
  guardId?: string; // undefined = create mode
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
}

interface GuardFormData {
  first_name: string;
  last_name: string;
  external_id: string;
  email: string;
  phone: string;
}

export default function GuardFormModal({ guardId, isOpen, onClose, onSuccess }: GuardFormModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!guardId;

  const [form, setForm] = useState<GuardFormData>({
    first_name: '', last_name: '', external_id: '', email: '', phone: '',
  });
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: guard, isLoading: guardLoading } = useQuery({
    queryKey: ['vendor', 'guards', guardId],
    queryFn: () => vendorApi.getGuard(guardId!).then((r) => r.data),
    enabled: isOpen && isEdit,
  });

  const { data: sitesData } = useQuery({
    queryKey: ['vendor', 'sites', 'all'],
    queryFn: () => vendorApi.getSites({ page: '1' }).then((r) => r.data),
    enabled: isOpen,
  });

  useEffect(() => {
    if (guard) {
      setForm({
        first_name: guard.first_name, last_name: guard.last_name,
        external_id: guard.external_id || '', email: guard.email || '', phone: guard.phone || '',
      });
      setSelectedSiteIds(guard.assigned_sites?.map((s) => s.id) ?? []);
    } else if (!isEdit) {
      setForm({ first_name: '', last_name: '', external_id: '', email: '', phone: '' });
      setSelectedSiteIds([]);
    }
  }, [guard, isEdit, isOpen]);

  const createMutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string; external_id?: string; email?: string; phone?: string }) =>
      vendorApi.createGuard(data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => vendorApi.updateGuard(guardId!, data),
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const payload: Record<string, unknown> = { ...form };
    if (!payload.external_id) delete payload.external_id;
    if (!payload.email) delete payload.email;
    if (!payload.phone) delete payload.phone;

    try {
      let resultId: string;
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        resultId = guardId!;
      } else {
        const response = await createMutation.mutateAsync(payload as { first_name: string; last_name: string });
        resultId = response.data.id;
      }

      // Handle site assignments
      const existingSiteIds = isEdit ? (guard?.assigned_sites?.map((s) => s.id) ?? []) : [];
      const newSiteIds = selectedSiteIds.filter((sid) => !existingSiteIds.includes(sid));
      if (newSiteIds.length > 0) {
        try {
          await vendorApi.assignGuardToSites(resultId, newSiteIds);
        } catch {
          // Guard saved but assignment failed
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vendor', 'guards'] }),
        queryClient.invalidateQueries({ queryKey: ['vendor', 'sites'] }),
      ]);
      onSuccess?.(resultId);
      onClose();
    } catch (err: unknown) {
      setError(extractApiError(err));
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const activeSites = (sitesData?.results ?? []).filter((s: SiteListItem) => s.vendor_assignment_status === 'active');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Guard' : 'Add Guard'} size="lg">
      {isEdit && guardLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
              <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </div>

          {activeSites.length > 0 && !isEdit && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Site Assignments</label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {activeSites.map((site: SiteListItem) => (
                  <label key={site.id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={selectedSiteIds.includes(site.id)}
                      onChange={() => setSelectedSiteIds((prev) => prev.includes(site.id) ? prev.filter((id) => id !== site.id) : [...prev, site.id])}
                      className="rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                    <span className="text-sm text-slate-900">{site.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSaving}
              className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50">
              {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Guard'}
            </button>
            <button type="button" onClick={onClose}
              className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
